import { useCallback, useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { ease, spring } from "../motion/tokens";

// Long enough that a warm cache doesn't flash the loader for a single frame,
// short enough that it is never felt as a gate. Anything above this is latency.
const MIN_DISPLAY_MS = 600;
const MAX_DISPLAY_MS = 8000;

const IntroLoader = ({ onComplete }) => {
  const videoRef = useRef(null);
  const progressRef = useRef(0);
  const resourcesLoadedRef = useRef(false);
  const animFrameRef = useRef(null);
  const startTimeRef = useRef(performance.now());
  const exitingRef = useRef(false);

  const prefersReducedMotion = useReducedMotion();
  const [isPresent, setIsPresent] = useState(true);

  // Progress lives in a MotionValue rather than state: it changes every frame,
  // and a React render per frame costs more than the bar it drives.
  const progress = useMotionValue(0);
  const percentLabel = useTransform(progress, (v) => `${Math.round(v * 100)}%`);

  const finishIntro = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;

    // Speed up video during exit for a dramatic finish
    if (videoRef.current) {
      videoRef.current.playbackRate = 2.0;
    }

    setIsPresent(false);
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      // Start video muted and playing
      video.muted = true;
      video.playsInline = true;
      video.play().catch(() => {
        // Autoplay blocked — nothing to watch, so don't hold the site back
        finishIntro();
      });
    }

    // Track total resources to estimate load progress
    let resourceTarget = 1;
    let resourceCount = 0;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      resourceCount += entries.length;
      resourceTarget = Math.max(resourceTarget, resourceCount + 5);
    });

    try {
      observer.observe({ type: "resource", buffered: true });
    } catch {
      // PerformanceObserver not supported
    }

    // Smooth progress animation. The rAF timestamp shares performance.now()'s
    // monotonic clock, so a system clock change can't stretch or skip the intro.
    const tick = (now) => {
      const elapsed = now - startTimeRef.current;
      const timeProgress = Math.min(elapsed / MAX_DISPLAY_MS, 1);
      const loaded = resourcesLoadedRef.current;
      const resourceProgress = loaded
        ? 1
        : Math.min(resourceCount / resourceTarget, 0.85);

      // Blend time-based and resource-based progress
      const target = loaded
        ? 1
        : Math.max(timeProgress * 0.3 + resourceProgress * 0.7, progressRef.current);

      // Ease the progress — never jump, always smooth. Once everything is in,
      // the bar closes the remaining gap fast instead of crawling to 100%.
      const smoothing = loaded ? 0.2 : 0.08;
      progressRef.current = Math.min(
        progressRef.current + (target - progressRef.current) * smoothing,
        1
      );
      progress.set(progressRef.current);

      // Adjust video playback rate based on load speed
      if (video && !video.paused) {
        const loadSpeed = loaded ? 1.5 : 0.4 + resourceProgress * 1.2;
        // Smooth the playback rate transition
        const currentRate = video.playbackRate;
        const targetRate = Math.max(0.25, Math.min(loadSpeed, 2.0));
        video.playbackRate = currentRate + (targetRate - currentRate) * 0.1;
      }

      // Check if we should finish
      if (loaded && elapsed >= MIN_DISPLAY_MS && progressRef.current >= 0.995) {
        finishIntro();
        return;
      }

      // Force finish at max time
      if (elapsed >= MAX_DISPLAY_MS) {
        finishIntro();
        return;
      }

      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);

    // Listen for full page load
    const handleLoad = () => {
      resourcesLoadedRef.current = true;
    };

    if (document.readyState === "complete") {
      resourcesLoadedRef.current = true;
    } else {
      window.addEventListener("load", handleLoad);
    }

    // Any key is an exit, not just Escape — a curtain nobody can lift is a trap.
    const handleKeyDown = () => finishIntro();
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("load", handleLoad);
      window.removeEventListener("keydown", handleKeyDown);
      observer.disconnect();
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [finishIntro, progress]);

  // Wall-clock escape hatch, deliberately in its own effect.
  //
  // Both finish conditions live inside the rAF tick, and browsers stop firing
  // rAF entirely in a hidden tab — so a site opened in a background tab
  // (cmd-click, "open in new tab") sits behind a full-screen curtain with its
  // progress frozen, and the MAX_DISPLAY_MS ceiling that exists to prevent
  // exactly that is never reached. A timer keeps running when rAF does not.
  //
  // It cannot share the effect above: that one re-runs on every `progress`
  // change, which would clear and restart this timer ~60×/s and guarantee it
  // never fires. Keyed on `finishIntro` alone, it is armed exactly once.
  useEffect(() => {
    const failsafe = setTimeout(finishIntro, MAX_DISPLAY_MS);
    return () => clearTimeout(failsafe);
  }, [finishIntro]);

  // Loop the video if it ends before loading finishes
  const handleVideoEnd = () => {
    if (!exitingRef.current && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {isPresent && (
        <motion.div
          className="intro-loader"
          // .intro-loader carries an 0.8s CSS transition on opacity/transform.
          // It would lag behind every frame written here, so the exit is driven
          // entirely by the motion values below.
          style={{ transition: "none" }}
          // The whole curtain is the skip target: any pointer input on a screen
          // the user did not ask for means "let me through".
          onPointerDown={finishIntro}
          // pointerEvents is not animatable, so framer sets it the instant the
          // exit starts — the fading curtain must not swallow the first click.
          exit={
            prefersReducedMotion
              ? { opacity: 0, pointerEvents: "none" }
              : { opacity: 0, scale: 1.05, pointerEvents: "none" }
          }
          transition={{
            duration: prefersReducedMotion ? 0.2 : 0.5,
            ease: ease.exit,
          }}
        >
          {/* Reduced motion gets no autoplaying full-viewport video — that is
              exactly the large moving background the setting asks us to drop. */}
          {!prefersReducedMotion && (
            <video
              ref={videoRef}
              className="intro-loader__video"
              src="/vct-intro.mp4"
              muted
              playsInline
              preload="auto"
              onEnded={handleVideoEnd}
            />
          )}
          <div className="intro-loader__overlay" />
          <div className="intro-loader__progress-container">
            <div className="intro-loader__progress-track">
              <motion.div
                className="intro-loader__progress-bar"
                // scaleX instead of width: composited, and it drops the class's
                // 0.3s width transition that would trail the value every frame.
                style={{
                  width: "100%",
                  scaleX: progress,
                  originX: 0,
                  transition: "none",
                }}
              />
            </div>
            <motion.span className="intro-loader__progress-text">
              {percentLabel}
            </motion.span>
          </div>
          <motion.button
            type="button"
            aria-label="Skip intro"
            onPointerDown={finishIntro}
            className="absolute bottom-6 right-6 z-[3] rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium uppercase tracking-[0.14em] text-white backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 0.5,
              transition: { delay: 0.7, duration: 0.35, ease: ease.enter },
            }}
            whileHover={{ opacity: 1, transition: spring.snap }}
            whileFocus={{ opacity: 1, transition: spring.snap }}
            whileTap={
              prefersReducedMotion
                ? { opacity: 0.7, transition: spring.press }
                : { opacity: 1, scale: 0.96, transition: spring.press }
            }
          >
            Skip
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroLoader;
